<script setup lang="ts">
import type { ProductOptionSelector } from '#components'
import type { OrderLineFragment, SearchProductVariantResultFragment, SearchResultFragment } from '#graphql-operations'
import type { MaybeComputedElementRef } from '@vueuse/core'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { COLOR_PRODUCT_OPTION_GROUP } from '@layers/common/app/constants'
import * as Sentry from '@sentry/nuxt'

const { ecommerce } = useAnalytics()
const { t, d } = useI18n()
const priceFormatter = usePriceFormatter()
const drawers = useDrawers()
const route = useRoute()
const { toast } = useToast()
const [DefineAddToCart, ReuseAddToCart] = createReusableTemplate()
const [DefineProductOptionSelector, ReuseProductOptionSelector] = createReusableTemplate()

const total = ref(0)
const isLoading = ref(false)
const isMounted = ref(false)
const vatModalOpen = ref(false)
const showBottomSheet = ref(false)
const bottomSheetScrollContainer = ref<HTMLElement | null>(null)
const { arrivedState } = useScroll(bottomSheetScrollContainer)

const activeOrderStore = useActiveOrderStore()
const rating = useRating()

const productStore = useProductStore()
const productId = computed(() => route.params.id as string)

if (import.meta.client) {
  onBeforeMount(() => {
    productStore.fetchProduct(productId.value).then(productStore.initializeFromQuery)
  })
}

onBeforeUnmount(productStore.reset)

const reviewRating = computed(() => {
  if (!productStore.product?.customProductMappings.reviewRating)
    return 0
  return Math.round(productStore.product.customProductMappings.reviewRating * 100) / 100
})

const reviewCount = computed(() => productStore.product?.customProductMappings.reviewCount ?? 0)

// Find the image index of the current variant (full selection or just color selection)
const selectedVariantImageIndex = computed(() => {
  // If all options are selected, use the selected variant's image
  if (productStore.selectedVariant?.featuredAsset?.id) {
    return productStore.images?.findIndex(image => image.id === productStore.selectedVariant?.featuredAsset?.id) ?? 0
  }

  // If only color is selected, find a variant with that color
  const colorOption = productStore.selectedOptions[COLOR_PRODUCT_OPTION_GROUP]
  if (colorOption) {
    const colorVariant = productStore.productVariants?.find(v =>
      v.options?.some(option => option.group.code === COLOR_PRODUCT_OPTION_GROUP && option.code === colorOption),
    )
    if (colorVariant?.featuredAsset?.id) {
      return productStore.images?.findIndex(image => image.id === colorVariant.featuredAsset?.id) ?? 0
    }
  }

  return 0
})

const currentImageIndex = ref(selectedVariantImageIndex.value)

const addingToCart = ref(false)
const addToCartButtonRef: MaybeComputedElementRef = ref(null)
const addToCartButtonVisible = useElementVisibility(addToCartButtonRef)
const showOptionErrors = ref(false)
const productOptionSelector = ref<ComponentExposed<typeof ProductOptionSelector>>()

async function handleAddToCart(selectedVariant?: SearchProductVariantResultFragment, quantity = 1) {
  if (productStore.status === 'pending' || addingToCart.value)
    return

  if (!productStore.allOptionsSelected) {
    showOptionErrors.value = true
    // Trigger wiggle animation on the option that isn't selected
    productOptionSelector.value?.wiggleUnselectedOptionGroups?.()
    Sentry.captureMessage('Not all product options selected', {
      extra: {
        productId: productStore.product?.id,
        productVariantId: selectedVariant?.id,
      },
    })
    toast({
      title: t('product.error.please_select_all_options'),
      variant: 'destructive',
    })
    return
  }

  if (!selectedVariant) {
    Sentry.captureMessage('No product variant selected', {
      extra: { productId: productStore.product?.id },
    })
    toast({
      title: t('product.error.variant_not_found'),
      variant: 'destructive',
    })
    return
  }

  addingToCart.value = true

  try {
    await activeOrderStore.addItemToOrder(selectedVariant, quantity)
    ecommerce.addToCart(selectedVariant, quantity)
    showBottomSheet.value = false
    drawers.value.cart.open = true
    ecommerce.viewCart()
  }
  catch (error) {
    Sentry.captureException(error)
    if (error instanceof Error)
      toast({ title: error.message, variant: 'destructive' })
  }
  finally {
    addingToCart.value = false
  }
}

async function handleAdjustOrderLine(orderLine: OrderLineFragment, quantity: number) {
  if (productStore.status === 'pending' || addingToCart.value)
    return

  addingToCart.value = true
  await activeOrderStore.adjustOrderLine(orderLine, quantity)
  addingToCart.value = false

  if (quantity > orderLine.quantity) {
    ecommerce.addToCart(orderLine, quantity)
    drawers.value.cart.open = true
    ecommerce.viewCart()
  }
  else {
    ecommerce.removeFromCart(orderLine, quantity)
  }
}

const runtimeConfig = useRuntimeConfig()

const now = useState(() => new Date())
const shippingTime = computed(() => {
  const calculateDate = (days: number) => d(new Date(now.value.getTime() + days * 24 * 60 * 60 * 1000), {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return {
    min: calculateDate(+runtimeConfig.public.shippingTime.min),
    max: calculateDate(+runtimeConfig.public.shippingTime.max),
  }
})

const seller = computed(() => {
  const seller = productStore.product?.facetValues?.find(facetValue => ['vendor', 'seller'].includes(facetValue.facet.code.toLowerCase()))
  if (!seller)
    return null

  return {
    id: seller.id,
    code: seller.code,
    name: seller.name,
    slug: createSlug(seller.name),
  }
})

const { soldCount } = useProductMetrics(productId.value)

const productHistoryStore = useProductHistoryStore()
const historyItems = computed(() => productHistoryStore?.history.filter(item => item.id !== productId.value))

function handleClick(item: SearchResultFragment) {
  productHistoryStore.addItemToHistory(item)
  ecommerce.selectItem(item)
}

const title = computed(() => productStore.product?.name ?? t('pages.title.index'))
const description = computed(() => stripHTMLTags(productStore.product?.description ?? ''))
const image = computed(() => productStore.product?.featuredAsset?.preview)

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogImage: image,
})

watch(() => productStore.status, (currentStatus, oldStatus) => {
  if (oldStatus === 'pending' && currentStatus === 'success' && !productStore.product) {
    showError({
      status: 404,
      statusText: t('error.product_not_found'),
    })
  }
})

const sizeChartModalOpen = ref(false)
const hasSizeChart = computed(() => productStore.product?.description?.includes('<table'))
const tables = computed(() => {
  if (typeof document !== 'undefined' && hasSizeChart.value && productStore.product?.description)
    return extractTablesFromHTML(productStore.product.description)
  return []
})
const showLightbox = ref(false)
const lightboxKey = ref(Date.now())
function openLightbox(index: number) {
  lightboxKey.value = Date.now()
  currentImageIndex.value = index
  showLightbox.value = true
}

// Helper function to get the correct variant image based on selection state
const variantImage = computed(() => {
  // If all options are selected, use the selected variant's image
  if (productStore.selectedVariant?.featuredAsset?.preview) {
    return productStore.selectedVariant.featuredAsset.preview
  }

  // If only color is selected, find a variant with that color
  const colorOption = productStore.selectedOptions[COLOR_PRODUCT_OPTION_GROUP]
  if (colorOption) {
    const colorVariant = productStore.productVariants?.find(v =>
      v.options?.some(option => option.group.code === COLOR_PRODUCT_OPTION_GROUP && option.code === colorOption),
    )
    if (colorVariant?.featuredAsset?.preview) {
      return colorVariant.featuredAsset.preview
    }
  }

  // Fallback to product image
  return productStore.product?.featuredAsset?.preview
})

const productRating = computed(() => {
  if (!productStore.product?.id)
    return 0
  return rating.getRating(productStore.product.id, 1, [4])
})

onMounted(async () => {
  isMounted.value = true
  window.scrollTo(0, 0)
})
</script>

<template>
  <DefineAddToCart>
    <AddToCart
      class="pt5"
      :loading="addingToCart"
      @add-to-cart="handleAddToCart(productStore.activeVariant)"
      @change:order-line="handleAdjustOrderLine"
    />
  </DefineAddToCart>

  <DefineProductOptionSelector>
    <ProductOptionSelector
      ref="productOptionSelector"
      v-model:show-option-errors="showOptionErrors"
      v-model:size-chart-modal-open="sizeChartModalOpen"
      :has-size-chart
    />
  </DefineProductOptionSelector>

  <div>
    <ProductDetailSkeleton v-if="productStore.status === 'pending'" />

    <div v-else-if="productStore.status === 'error'">
      {{ t('error.404') }}
    </div>

    <div v-else>
      <div v-if="productStore.product?.id && !addToCartButtonVisible && isMounted" class="pb-safe sm:hidden">
        <div class="fixed bottom-0 left-0 right-0 z50 border-t n-border-base bg-white p4 pb6 shadow">
          <AddToCartButton
            :loading="addingToCart"
            :skeleton="addingToCart"
            @click="showBottomSheet = true"
          />
        </div>
      </div>

      <main class="mx-auto max-w-3xl px4 lg:max-w-[88rem] md:max-w-4xl lg:px6 sm:px4 sm:px6">
        <!-- Product -->
        <div class="mb6">
          <div class="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-10 sm:pt8">
            <div class="lg:col-span-4 lg:row-end-1 -mx4 md:mx0">
              <ProductGallery
                :images="productStore.images"
                :image-index="selectedVariantImageIndex"
                @update:image-index="currentImageIndex = $event"
              >
                <div class="absolute left-2 top-2 z1">
                  <div class="flex flex-col items-start space-y-2">
                    <NTip
                      n="xs" icon="i-heroicons:truck"
                      class="bg-emerald-200:80 text-emerald-700 font-bold backdrop-blur"
                    >
                      {{ t('policies.free_shipping') }}
                    </NTip>
                    <NTip
                      v-if="productStore.amountSaved > 0" n="xs" icon="i-ri:hand-coin-line"
                      class="bg-orange-200:80 text-orange-700 font-bold backdrop-blur"
                    >
                      {{
                        t('product.you_save.label', [priceFormatter(productStore.amountSaved, productStore.product.currency)])
                      }}
                    </NTip>
                  </div>
                </div>
                <div
                  class="pointer-events-auto absolute start-0 bottom-0 z-20 flex flex-col cursor-pointer items-start px4 pb3.75 pt4 transition-all duration-300 ease"
                  @click="openLightbox(currentImageIndex)"
                >
                  <div class="group pointer-events-auto inline-flex cursor-pointer align-middle">
                    <button
                      class="h12 min-w-12 inline-flex flex-nowrap cursor-pointer items-center justify-center of-hidden rounded-full bg-white text-sm !p0 hover:c-slate-500"
                      @click="openLightbox(currentImageIndex)"
                    >
                      <span class="h12 w12 inline-flex items-center justify-center">
                        <Icon name="i-heroicons:arrows-pointing-out" class="size-5.5" />
                      </span>
                      <span
                        class="max-w-0 of-hidden whitespace-nowrap p0 fw600 transition-max-width,padding duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.15)] group-hover:max-w-[280px] group-hover:pe-[25px]"
                      >
                        {{ t('gallery.click_to_enlarge') }}
                      </span>
                    </button>
                  </div>
                </div>
              </ProductGallery>

              <ProductGalleryLightbox
                v-if="productStore.images"
                :key="lightboxKey"
                v-model:show="showLightbox"
                :images="productStore.images"
                :image-index="currentImageIndex"
              />

              <ProductReviews
                class="mt8 hidden lg:block"
                :review-count
                :review-rating
                :product-id="productId"
                :product-variant-id="productStore.selectedVariant?.id"
              />
            </div>

            <!-- Product details -->
            <div class="mx-auto mt2 max-w-2xl w-full lg:col-span-3 lg:row-span-2 lg:row-end-2 lg:mt0 lg:max-w-none">
              <div class="flex-col px0 space-y-1 lg:px0 md:px4 sm:px6 md:space-y-4">
                <!-- Title -->
                <div>
                  <ProductBreadcrumbs v-if="productStore.productBreadcrumbs?.length" :items="productStore.productBreadcrumbs" class="hidden md:block" />

                  <h1 class="fw600 md:text-base" data-testid="productName" v-text="productStore.product.name" />

                  <!-- Rating -->
                  <div class="flex items-center justify-between gap-x-1 gap-y-1 py-1 md:justify-start md:gap-x-2">
                    <h2 class="sr-only" v-text="t('general.reviews.label')" />
                    <div
                      class="flex gap-x-1 rd-md py-0.5 sm:flex-row sm:from-success/20 sm:to-transparent sm:bg-gradient-to-r sm:pl-1 md:gap-x-2"
                    >
                      <!-- Verified Purchase -->
                      <div class="flex shrink-0 items-center">
                        <Icon name="lucide:badge-check" class="text-success" />
                        <p class="ms-0.5 text-sm text-success font-semibold">
                          {{ t('product.rating.verified_purchases') }}
                        </p>
                      </div>

                      <!--  - Dot Separator -
                    <div class="mx-0.5 text-sm text-slate-400">
                      •
                    </div>
-->
                      <!-- Rating -->
                      <div class="flex shrink-0 items-center">
                        <Rating :rating="reviewRating || productRating" />
                        <p class="ms-1 mt-1 text-sm text-slate-400 font-semibold hidden md:mt0 sm:block">
                          {{ t('product.rating.rating.x_out_of_y.label', [reviewRating || productRating, 5]) }}
                        </p>
                      </div>
                    </div>

                    <!-- Dot Separator -->
                    <div class="mx-1 text-sm text-slate-400 hidden sm:block">
                      •
                    </div>

                    <!-- Sold Counter with Tooltip -->
                    <div class="flex shrink-0 items-center text-sm text-slate-400 lowercase">
                      {{ t('product.sold_counter.label', [soldCount]) }}
                      <UiTooltipProvider>
                        <UiTooltip>
                          <UiTooltipTrigger>
                            <Icon name="i-mdi:information-slab-circle-outline" class="ml-1 text-base text-slate-400 -mt-0.5" />
                          </UiTooltipTrigger>
                          <UiTooltipContent>
                            {{ t('product.sold_counter.info', [soldCount]) }}
                          </UiTooltipContent>
                        </UiTooltip>
                      </UiTooltipProvider>
                    </div>
                  </div>
                </div>
                <!-- Price -->
                <div>
                  <div class="flex flex items-center justify-between md:justify-start md:space-x-2">
                    <div class="flex items-center justify-start space-x-2">
                      <span class="text-xl text-context font-bold md:text-2xl" n="slate9 dark:slate4">
                        {{ priceFormatter(productStore?.price, productStore.product?.currency) }}
                      </span>
                      <template v-if="productStore?.priceBeforeDiscount && productStore.priceBeforeDiscount > productStore?.price">
                        <span class="text-base text-muted-foreground line-through md:text-xl">
                          {{ priceFormatter(productStore.priceBeforeDiscount, productStore.product?.currency) }}
                        </span>
                        <NBadge v-if="productStore.discountPercentage" n="xs orange5" class="rd-md fw600 tracking-wider">
                          -{{ productStore.discountPercentage }}%
                        </NBadge>
                      </template>
                    </div>
                  </div>

                  <NButton
                    id="vat-info"
                    icon="i-heroicons:information-circle-solid"
                    n="borderless xs slate5 dark:slate7"
                    class="px1 py0 text-context fw600 hidden lg:block"
                    @click="vatModalOpen = true"
                  >
                    {{ t('product.vat_included.label') }}
                  </NButton>

                  <!-- Order Countdown
                  <div class="mt-2">
                    <OrderCountdown />
                  </div> -->
                </div>

                <USPHighlights :product-id="productStore.product?.id ?? ''" />

                <!-- Variant -->
                <div data-testid="productForm">
                  <div>
                    <ReuseProductOptionSelector />
                  </div>

                  <!-- Add to cart -->
                  <ReuseAddToCart ref="addToCartButtonRef" />
                </div>

                <!-- Policies -->
                <USPPolicies />

                <div class="mb4 block bg-slate/30 pb4 shadow-inner -mx4 sm:hidden" />

                <div>
                  <ClientOnly>
                    <UiAccordion type="single" collapsible default-value="description">
                      <UiAccordionItem value="description">
                        <UiAccordionTrigger>
                          <span class="fw600">
                            {{ t('product.description.label') }}
                          </span>
                        </UiAccordionTrigger>
                        <UiAccordionContent>
                          <ProductDescription :description="productStore.product?.description ?? ''" />
                        </UiAccordionContent>
                      </UiAccordionItem>
                      <UiAccordionItem v-if="tables.length" value="size-chart">
                        <UiAccordionTrigger>
                          <span class="fw600"> {{ t('product.size_chart.title') }}</span>
                        </UiAccordionTrigger>
                        <UiAccordionContent>
                          <div class="py4">
                            <div
                              v-for="(table, index) in tables" :key="index"
                              class="flex of-x-auto border n-border-200 rounded-lg border-solid bg-background space-y-2"
                            >
                              <ProductSizeChart :table="table" />
                            </div>
                          </div>
                        </UiAccordionContent>
                      </UiAccordionItem>
                      <UiAccordionItem v-if="seller?.name" value="seller">
                        <UiAccordionTrigger>
                          <span class="fw600"> {{ t('general.about_seller.label') }}</span>
                        </UiAccordionTrigger>
                        <UiAccordionContent>
                          <div v-if="productStore.product?.name && seller && seller?.name" class="flex flex-col gap-y-3">
                            <div class="flex gap-x-2">
                              <span>
                                {{ t('general.seller.label') }}:
                              </span>
                              <NLink :to="`/stores/${seller.id}/${seller.slug}`">
                                {{ seller.name }}
                              </NLink>
                            </div>
                            <NButton :to="`/stores/${seller.id}/${seller.slug}`" class="w-fit px2 py0 text-sm" n="sm">
                              {{ t('general.show_whole_seller_inventory.label') }}
                              <Icon name="i-heroicons:arrow-right" />
                            </NButton>
                          </div>
                        </UiAccordionContent>
                      </UiAccordionItem>
                      <UiAccordionItem value="shipping-and-delivery">
                        <UiAccordionTrigger>
                          <span id="shipping-and-delivery" class="fw600"> {{ t('shipping.shipping_and_delivery.title') }}</span>
                        </UiAccordionTrigger>
                        <UiAccordionContent>
                          <ul>
                            <li>
                              <p class="mt2">
                                <strong class="uppercase">{{ t('shipping.express') }}:&nbsp;</strong>
                                <span>
                                  {{ t('shipping.delivery_between', [`${shippingTime.min} - ${shippingTime.max}`]) }}
                                </span>
                              </p>
                            </li>
                          </ul>
                        </UiAccordionContent>
                      </UiAccordionItem>
                    </UiAccordion>
                  </ClientOnly>
                </div>

                <ProductReviews
                  class="mt8 border-none shadow-none [&>div]:px0 lg:hidden"
                  :review-count
                  :review-rating
                  :product-id="productId"
                  :product-variant-id="productStore.selectedVariant?.id"
                />
              </div>
            </div>
          </div>
        </div>

        <MarketingPrizeBanner />

        <div class="my4 block bg-slate/30 pb4 shadow-inner -mx4 sm:hidden" />

        <MarketingSatisfactionGuarantee class="n-border-200 lg:border-t" />

        <div class="px4 py6 sm:mb8 lg:px8 sm:px6">
          <div class="w-full inline-flex items-center justify-center">
            <span class="relative h-3 w-3 flex">
              <span class="absolute h-full w-full inline-flex animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span class="relative h-3 w-3 inline-flex rounded-full bg-emerald-500" />
            </span>
            <div class="ml3 text-context fw600" n="base emerald8">
              {{ t('policies.satisfaction_guarantee.customer_support.we_are_here_for_you') }}
            </div>
          </div>

          <div class="text-center">
            <NButton
              id="contact-support"
              icon="i-heroicons:envelope"
              :href="`mailto:${$config.public.support.emailAddress}`"
              class="mt2 w-full justify-center sm:w-auto"
            >
              {{ $config.public.support.emailAddress }}
            </NButton>
          </div>
        </div>
      </main>

      <div class="mx-auto max-w-3xl px2 pb8 pt2 lg:max-w-[88rem] lg:px6 md:max-w-4xl sm:px4 sm:pb12 sm:pt6">
        <ClientOnly>
          <section v-if="historyItems?.length" aria-labelledby="viewed-products-title" class="mb12">
            <div>
              <h2 id="viewed-products-title" class="text-base fw600" v-text="t('product.recently_viewed.title')" />
            </div>

            <div class="mb6">
              <div class="grid grid-cols-2 mt6 gap-x-2 gap-y-4 sm:grid-cols-minmax-12rem" data-testid="productGrid">
                <ProductCard
                  v-for="item in historyItems" :key="item.id" :item="item"
                  :data-testid="`productGridItem-${item.id}`" @click="handleClick(item)"
                />
              </div>
            </div>
          </section>

          <section v-if="productStore.productCollectionId" aria-labelledby="related-products-title">
            <div>
              <h2 id="related-products-title" class="text-base fw600" v-text="t('product.related_items.title')" />
            </div>
            <LazyProductsGrid
              :search="{ collectionId: productStore.productCollectionId }"
              @total="(v) => (total = v)"
              @loading="(loading) => (isLoading = loading)"
            />
          </section>
        </ClientOnly>
      </div>

      <ClientOnly>
        <NBottomSheet
          v-if="productStore.product?.id"
          :show="showBottomSheet"
          closable
          @close="showBottomSheet = false"
        >
          <template #header>
            <div
              class="w-full flex items-center justify-between px4 py3"
              :class="{ 'border-b n-border-base': !arrivedState.top }"
            >
              <div class="flex items-center">
                <NuxtImg
                  :src="variantImage"
                  :alt="productStore.product?.name"
                  class="h16 w16"
                  width="100"
                  height="100"
                  loading="lazy"
                />
                <div class="ml2">
                  <div class="line-clamp-1 overflow-hidden text-context" n="sm slate5 dak:slate4">
                    {{ productStore.product.name }}
                  </div>

                  <div
                    v-if="productStore.selectedVariant?.currency && productStore.selectedVariant?.price"
                    class="flex items-center justify-start space-x-2"
                  >
                    <span class="text-context font-bold" n="xl slate9 dark:slate4">
                      {{ priceFormatter(productStore.price, productStore.selectedVariant.currency) }}
                    </span>
                    <template v-if="productStore?.priceBeforeDiscount && productStore.priceBeforeDiscount > productStore.selectedVariant.price">
                      <span class="text-context line-through" n="slate5 lg">
                        {{ priceFormatter(productStore.priceBeforeDiscount, productStore.selectedVariant.currency) }}
                      </span>
                      <NBadge v-if="productStore.discountPercentage" n="xs orange5" class="fw600 tracking-wider !rd-full">
                        -{{ productStore.discountPercentage }}%
                      </NBadge>
                    </template>
                  </div>

                  <NButton
                    icon="i-heroicons:information-circle-solid"
                    n="borderless sm slate5 dark:slate7"
                    class="px1 py0 text-context fw600"
                    @click="vatModalOpen = true"
                  >
                    {{ t('product.vat_included.label') }}
                  </NButton>

                  <!-- Order Countdown for mobile -->
                  <div class="mt-2">
                    <OrderCountdown />
                  </div>
                </div>
              </div>

              <div class="h7 flex items-center -mt8">
                <NButton
                  id="close-bottom-sheet"
                  n="lg borderless"
                  icon="i-heroicons:x-mark"
                  class="p-0.25em children:m-0 sm:-m-2"
                  @click="showBottomSheet = false"
                />
              </div>
            </div>
          </template>

          <div ref="bottomSheetScrollContainer" class="flex-1 overflow-y-auto">
            <div class="mb8 px4 pb2">
              <ReuseProductOptionSelector />
            </div>
          </div>

          <template #footer>
            <div class="pb-safe">
              <div class="p4 pb6">
                <ReuseAddToCart />
              </div>
            </div>
          </template>
        </NBottomSheet>

        <NModal :open="vatModalOpen" @close="vatModalOpen = false">
          <HDialogPanel
            class="max-w-md w-full transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
          >
            <div class="absolute right-0 top-0 pr4 pt4">
              <NButton
                id="close-vat-info"
                n="lg borderless"
                icon="i-heroicons:x-mark"
                class="p-0.25em text-context children:m-0"
                @click="vatModalOpen = false"
              />
            </div>

            <div>
              <div class="mx-auto h12 w12 flex items-center justify-center rounded-full bg-orange-50 text-orange5 dark:bg-orange-900">
                <Icon name="i-heroicons:receipt-percent-solid" class="text-2xl" />
              </div>

              <div class="mt3 text-center sm:mt5">
                <h3 id="modal-title" class="text-base c-slate9 font-semibold leading-6">
                  {{ t('product.vat_included.title') }}
                </h3>

                <div class="mt2">
                  <p n="sm slate5 dark:slate7" class="text-context">
                    {{ t('product.vat_included.body') }}
                  </p>
                </div>
              </div>
            </div>
          </HDialogPanel>
        </NModal>
      </ClientOnly>

      <template v-if="tables?.length">
        <NModal :show="sizeChartModalOpen" @close="sizeChartModalOpen = false">
          <HDialogPanel
            class="max-w-3xl w-full transform overflow-hidden rounded-md bg-white p3 text-left align-middle shadow-xl transition-all md:p6"
          >
            <div class="absolute right-0 top-0 pr4 pt4">
              <NButton
                id="close-size-table"
                n="lg borderless"
                icon="i-heroicons:x-mark"
                class="p-0.25em text-context children:m-0"
                @click="sizeChartModalOpen = false"
              />
            </div>

            <div class="text-center">
              <h3 id="modal-title" class="text-lg c-slate9 font-semibold leading-6">
                {{ t('product.size_chart.title') }}
              </h3>

              <div class="py4">
                <div
                  v-for="(table, index) in tables" :key="index"
                  class="mt4 of-x-auto border n-border-200 rounded-lg border-solid bg-background"
                >
                  <ProductSizeChart :table="table" />
                </div>
              </div>

              <div>
                {{ t('product.size_chart.note') }}
              </div>
            </div>
          </HDialogPanel>
        </NModal>
      </template>
    </div>
  </div>
</template>
